import Vue from 'vue'

import store from '../../store'
import moment from 'moment'


export default class IOTDevice {
  constructor(device_guid, device_attributes = false) {
    this.device_guid = device_guid
    this.device_name = this.device_guid // this gets overwritten, but is a good fallback
    this.device_type = false
    
    // basic state
    this.ready = false
    this.error = false
    this.errorText = ''
        
    this.controllable = false
    this.editable = false
    
    this.state = false
    this.state_timestamp = false
    
    this.metadata = false
    
    this.usersFetched = false
    this.users = []
    /** @type {number} pagination size for GET /devices/{guid}/users */
    this.itemsPerPage = 50
    this.userInvites = []
    
    this.licensesFetched = false
    this.licenses = []
    
    this.updatesAvailable = {}
    
    this.documentSyncing = false
    
    this.commandResponseCheckInterval = 2000
    this.commandResponseCheckMaxAttempts = 100
    this.commandResponseCheckAttempt = 0
    this.commandResponseTimer = false
    this.commandGUID = ''
    
    this.command = ''
    this.command_status = false
    this.command_message = ''
    
    this.last_api_request_id = ''
    this.last_state_update = false
    
    // object properties
    this.serial_number = (device_attributes) ? device_attributes.serial_number : 'Unknown'
    this.mac_address = (device_attributes) ? device_attributes.mac_address : '00:00:00:00:00:00'
    this.partner_id = (device_attributes && device_attributes.partner_id) ? device_attributes.partner_id : ''
    
    this.product_name = (device_attributes.product_name) ? device_attributes.product_name : 'unknown'
    this.provision_date = (device_attributes && device_attributes.provision_date) ? device_attributes.provision_date : ''
    
    this.org_guid = (device_attributes && device_attributes.org_guid) ? device_attributes.org_guid : false
    this.org_name = (device_attributes && device_attributes.org_name) ? device_attributes.org_name : false
    
    // device specific things
    this.shadowDocuments = []
    
    // IP related
    this.device_ip = ''
    this.external_ip = ''
    this.ip_scheme = ''
  }
  
  refresh() {
    this.fetchDevice()
    this.fetchMetadata()
    this.syncDocuments()
    this.fetchUsers()
    this.fetchLicenses()
  }
  
  refreshProperties() {
    this.deviceName(true)
    this.deviceIP(true)
    this.IPScheme(true)
  }
  
  delete() {
    clearTimeout(this.commandResponseTimer)
    
    this.device_guid = false
    this.device_name = 'deleted'
    
    this.autoRefresh = false
    this.state = false
    
    Vue.delete(this) // this shouldnt work.. but it does...
  }
  
  // accessibility methods
  icon() {
    return '$videon_logo'
  }
  
  deviceName(refresh = false) {
    if (this.device_name && !refresh) {
      return this.device_name
    }
    
    if (this.metadata && this.hasMetadataKey('device_alias')) {
      this.device_name = Vue.helpers.htmlDecode(this.metadataValue('device_alias'))
      return this.device_name
    }
    
    this.device_name = this.serial_number + ' [' + this.mac_address + ']'
    return this.device_name
  }
  
  isReady() {
    if (this.state_timestamp && this.licensesFetched) {
      this.ready = true
    }
    
    return this.ready
  }
  
  isOnline() {
    if (this.state) {
      return this.state.online
    }
    return false
  }
  
  deviceIP(refresh = false) {
    if (this.device_ip && !refresh) {
      return this.device_ip
    }
    
    if (this.state) {
      if (this.state.device_ip) {
        this.device_ip = this.state.device_ip
      }
      if (this.state.external_ip) {
        this.external_ip = this.state.external_ip
      }
    }
    
    return this.device_ip
  }
  
  IPScheme(refresh = false) {
    if (this.ip_scheme && !refresh) {
      return this.ip_scheme
    }
    
    if (this.state && this.state.ip_scheme) {
      this.ip_scheme = this.state.ip_scheme
      return this.ip_scheme
    }
    
    return false
  }
  
  hasXMLConfig() {
    return false
  }
  
  hasUpdates() {
    for (var key in this.updatesAvailable) {
      if (this.updatesAvailable[key]) {
        return true
      }
    }
    return false
  }
  
  isUpdating() {
    return false
  }
  
  isCommandProcessing() {
    if (
      this.command_status 
      && this.command_status != 'complete' 
      && this.command_status != 'error'
      && this.command_status != 'rejected'
      && this.command_status != 'failed' 
    ) {
      return true
    }
    
    return false 
  }
  
  canControl() {
    // if we dont have any state, the device likely cant be interacted with
    if (!this.state) {
      return false
    }
  
    if (!this.usersFetched) {
      this.fetchUsers()
    }
    
    return this.controllable
  }

  canEdit() {
    // if we dont have any state, the device likely cant be interacted with
    if (!this.state) {
      return false
    }
  
    if (!this.usersFetched) {
      this.fetchUsers()
    }
    
    return this.editable
  }
  
  // metadata
  fetchMetadata() {
    if (!this.device_guid) {
      return
    }
    
    Vue.axios.get('/devices/' + this.device_guid + '/metadata')
    .then((response) => {
      console.log('VideonDevice ' + this.device_guid + ' fetchMetadata response', response)
      this.last_api_request_id = response.data.api_request_id
      if (response.data.metadata) {
        if (response.data.metadata.length > 0) {
          this.metadata = response.data.metadata
        } else {
          this.metadata = false
        }
        
        // refresh device name & ready status
        this.deviceName(true)
        this.isReady()
      }
    }).catch((error) => {
      console.log('VideonDevice ' + this.device_guid + ' fetchMetadata error', error)
    })
  }
  
  hasMetadataKey(key) {
    if (this.metadata && this.metadata.length > 0) {
      return (this.metadata.find(data => data.key === key && data.value)) ? true : false
    }
    return false
  }
  
  metadataValue(key) {
    if (this.metadata && this.metadata.length > 0) {
      var data = this.metadata.find(data => data.key === key)
      if (data) {
        return data.value
      }
    }
    return false
  }
  
  // documents & state
  syncDocuments(force = false) {
    if (!this.device_guid) {
      return
    }
    
    // stop multiple concurrent requests
    if (this.documentSyncing == true && !force) {
      console.log('VideonDevice ' + this.device_guid + ' syncDocuments already running')
      return
    }
    
    if (force || !this.ready || this.isUpdating() || document.hasFocus()) {
      this.documentSyncing = true
      
      Vue.axios.get('/devices/' + this.device_guid + '/documents')
      .then((response) => {
        // device has since been deleted
        if (!this.device_guid) {
          return
        }
        
        this.documentSyncing = false
        
        console.log('VideonDevice ' + this.device_guid + ' syncDocuments response', response)
        this.last_api_request_id = response.data.api_request_id
      
        if (!response.data.documents) {
          // fallback and fetch just state if we dont have it
          if (!this.state) {
            this.fetchState()
          }
          return
        }
        
        // compare document last_modified...
        var documents = response.data.documents
        
        for (var key in documents) {
          if (documents[key].last_modified) {
            var shadowName = key.split('/')[1]
            
            var newDocumentTimestamp = moment(documents[key].last_modified)
            
            if (key == 'state') {
              if (this.error || !this.state_timestamp || newDocumentTimestamp.isAfter(this.state_timestamp)) {
                console.log('VideonDevice ' + this.device_guid + ' syncDocuments updated State')
                this.state_timestamp = documents[key].last_modified
                this.fetchState()
              }
            } else {
              if (this.shadowDocuments.includes(shadowName)) {
                var shadowDocument = Vue.deviceShadows.getDeviceShadow(this.device_guid, shadowName)
                //console.log('VideonDevice ' + this.device_guid + ' syncDocuments ' + shadowName + ' has new timestamp of ' + documents[key].last_modified + ' and old timestamp of ' + shadowDocument.reported.timestamp)
                
                if ((!shadowDocument.reported && this.isOnline()) || newDocumentTimestamp.isAfter(shadowDocument.reported.timestamp)) {
                  console.log('VideonDevice ' + this.device_guid + ' syncDocuments updated ' + shadowName + ' shadow')
                  shadowDocument.fetchShadow()
                }
              } else {
                console.log('VideonDevice ' + this.device_guid + ' syncDocuments updated ' + shadowName + ' does not exist in device.shadowDocuments', this.shadowDocuments)
              }
            }
          }
        }
        
        // update ready status
        this.isReady()
      }).catch((error) => {
        console.log('VideonDevice ' + this.device_guid + ' syncDocuments error', Vue.helpers.parseError(error))
        
        this.error = true
        this.errorText = Vue.helpers.parseError(error)
      
        // fallback and fetch just state if we dont have it
        if (!this.state) {
          this.fetchState()
        }
      })
    }
  }
  
  fetchState() {
    if (!this.device_guid) {
      return
    }
    
    Vue.axios.get('/devices/' + this.device_guid + '/state')
    .then((response) => {
      console.log('VideonDevice ' + this.device_guid + ' fetchState response', response)
      this.last_api_request_id = response.data.api_request_id
      
      this.state = response.data.state
      
      Object.keys(this.state).map((key) => {
        if (this.state[key] == 'false') {
          this.state[key] = false
        } else if (this.state[key] == 'true') {
          this.state[key] = true
        }
      })
      
      // update the state_timestamp to now so that we dont fetch it twice on load
      this.state_timestamp = moment()
      
      if (this.state.last_state_update) {
        this.last_state_update = this.state.last_state_update
      }
      
      if (this.state.cloud_version) {
        // current version & check for updates
        this.cloud_version = Vue.helpers.parseVersion('cloud', this.state.cloud_version)
      }
      
      this.refreshProperties()
      
      // update ready status
      this.isReady()
      
      this.afterStateUpdate()
      
      this.error = false
      this.errorText = ''
    }).catch((error) => {
      this.error = true
      this.errorText = Vue.helpers.parseError(error)
      
      this.state_timestamp = moment()
      
      this.isReady()
      
      this.afterStateUpdate()
    })
  }
  
  afterStateUpdate() {
    
  }
  
  hasCompleteState() {
    if (
      this.state
      && this.state.last_boot
      && (this.state.ready_for_commands !== undefined)
      && this.state.last_state_update
    ) {
      return true
    }
    return false
  }
  
  
  /**
   * GET /devices/{guid}/users can return the same user_guid multiple times
   * (direct device row, org membership row, fleet membership row). Merge to
   * one row per user with most-permissive access; attach org_guid or fleet_guid
   * when that scope wins a tie at the max level (device > org > fleet).
   */
  mergeDeviceUserInheritanceRows(rawRows) {
    const byGuid = new Map()
    for (const r of rawRows || []) {
      const g = r.user_guid
      if (!g) {
        continue
      }
      if (!byGuid.has(g)) {
        byGuid.set(g, { direct: null, org: null, org_guid: null, fleets: [] })
      }
      const e = byGuid.get(g)
      if (r.fleet_guid) {
        e.fleets.push({ fleet_guid: r.fleet_guid, access: Number(r.access) })
      } else if (r.org_guid) {
        e.org = Number(r.access)
        e.org_guid = r.org_guid
      } else {
        e.direct = Number(r.access)
      }
    }

    const priority = { device: 3, org: 2, fleet: 1 }
    const out = []
    const sortedEntries = [...byGuid.entries()].sort((a, b) =>
      String(a[0]).localeCompare(String(b[0]))
    )

    for (const [user_guid, e] of sortedEntries) {
      const parts = []
      if (e.direct != null) {
        parts.push(['device', e.direct, null, null])
      }
      if (e.org != null) {
        parts.push(['org', e.org, e.org_guid, null])
      }
      if (e.fleets.length) {
        const maxFleet = Math.max(...e.fleets.map((f) => f.access))
        const fleetGuidsAtMax = e.fleets
          .filter((f) => f.access === maxFleet)
          .map((f) => f.fleet_guid)
          .sort()
        parts.push(['fleet', maxFleet, null, fleetGuidsAtMax[0]])
      }
      if (parts.length === 0) {
        continue
      }
      const eff = Math.max(...parts.map((p) => p[1]))
      const winners = parts.filter((p) => p[1] === eff)
      const winner = winners.reduce((a, b) =>
        priority[a[0]] >= priority[b[0]] ? a : b
      )
      const row = { user_guid, access: eff }
      if (winner[0] === 'org' && winner[2]) {
        row.org_guid = winner[2]
      }
      if (winner[0] === 'fleet' && winner[3]) {
        row.fleet_guid = winner[3]
      }
      out.push(row)
    }
    return out
  }

  // users & invites
  async fetchUsers() {
    if (!this.device_guid) {
      return
    }
    
    this.usersFetched = true
    
    var newUsers = []
    
    var queryParams = {
      'pagination_size': this.itemsPerPage
    }
    
    var paginationToken = false
    
    try {
      do {
        if (paginationToken) {
          queryParams.pagination_token = paginationToken
        }
        
        let response = await Vue.axios.get('/devices/' + this.device_guid + '/users', {'params': queryParams})
        console.log('VideonDevice ' + this.device_guid + ' fetchUsers response', response)

        if (response.data.users && response.data.users.length > 0) {
          newUsers = newUsers.concat(response.data.users)
        }
        if (response.data.pagination_token) {
          paginationToken = response.data.pagination_token
        } else {
          paginationToken = false
        }
      } while (paginationToken)
      
    } catch (error) {
      console.log('VideonDevice ' + this.device_guid + ' fetchUsers error', error)
      
      if (error.status == 404) {
        this.users = []
        this.resolveUserPermissions()
      }
    }
    
    this.usersFetched = true
    this.users = this.mergeDeviceUserInheritanceRows(newUsers)

    this.resolveUserPermissions()
  }
  
  fetchUserInvites() {
    if (!this.device_guid) {
      return
    }
    
    Vue.axios.get('/invites', {params: {'device_guid': this.device_guid}}).then((response) => {
      console.log('VideonDevice ' + this.device_guid + ' fetchUserInvites response', response)
      this.last_api_request_id = response.data.api_request_id
      
      this.userInvites = response.data.invites
    }).catch(() => {
      // do nothing...
    })
  }
  
  resolveUserPermissions() {
    if (store.getters['user/isDeviceAdmin']) {
      this.editable = true
      this.controllable = true
      return
    }
    
    let myGUID = store.getters['user/guid']
    
    for (var i in this.users) {
      var user = this.users[i]
      // if this user has appropriate permissions enable editable flag
      if (myGUID == user.user_guid && user.access > 200) {
        console.log('VideonDevice ' + this.device_guid + ' resolveUserPermissions user can edit device')
        this.editable = true
        this.controllable = true
        return
      } else if (myGUID == user.user_guid && user.access > 100) {
        console.log('VideonDevice ' + this.device_guid + ' resolveUserPermissions user can control device')
        this.controllable = true
        return
      }
    }
  }
  
  
  // licenses & entitlements
  fetchLicenses() {
    if (!this.device_guid) {
      return
    }
    
    Vue.axios.get('/entitlements/devices/' + this.device_guid + '/licenses').then((response) => {
      console.log('VideonDevice ' + this.device_guid + ' fetchLicenses response', response)
      this.last_api_request_id = response.data.api_request_id
      
      if (response.data.licenses) {
        this.licenses = response.data.licenses
        
        // warm the cache...
        for (var license of this.licenses) {
          Vue.entitlementProfiles.getEntitlementProfile(license.profile_guid)
        }
        
        this.licensesFetched = true
        this.isReady()
      }
      
      // test entitlements
      setTimeout(() => {
        console.log('VideonDevice ' + this.device_guid + ' fetchLicenses entitlements', this.entitlements())
      }, 10)
    }).catch((error) => {
      console.log('VideonDevice ' + this.device_guid + ' fetchLicenses error', Vue.helpers.parseError(error))
      this.licensesFetched = true
      this.isReady()
    })
  }
  
  // entitlements related
  capabilities() {
    return Vue.capabilities.getCapabilityProfile(this.product_name)
  }
  
  entitlements() {
    var entitlements = {}
    
    var entitlementProfileGUIDs = this.licenses.filter(license => !license.expired).map(license => { return license.profile_guid })
    
    // Add base entitlements
    if (this.device_type == 'encoder') {
      if (this.product_name == 'edgecaster_max') {
        entitlementProfileGUIDs.unshift('BASE_EDGECASTER_MAX')
      } else {
        entitlementProfileGUIDs.unshift('BASE_EDGECASTER')
      }
    }
    
    for (var profile_guid of entitlementProfileGUIDs) {
      var entitlement = Vue.entitlementProfiles.getEntitlementProfile(profile_guid)
      
      if (entitlement.ready) {
        for (var propertyKey in entitlement.properties) {
          var propertyValue = entitlement.properties[propertyKey]
          
          try {
            if (entitlements[propertyKey]) {
              if (typeof propertyValue === 'boolean') {
                entitlements[propertyKey] = propertyValue || entitlements[propertyKey]
              } else if (typeof propertyValue === 'number') {
                var minValueKeys = ['services.cloud.metrics.send_rate']
                
                if (minValueKeys.includes(propertyKey)) {
                  entitlements[propertyKey] = Math.min(propertyValue, entitlements[propertyKey])
                } else {
                  entitlements[propertyKey] = Math.max(propertyValue, entitlements[propertyKey])
                }
              } else if (typeof propertyValue === 'object') {
                if (propertyValue instanceof Array) {
                  entitlements[propertyKey] = [...new Set([...entitlements[propertyKey], ...propertyValue])]
                } else if (propertyValue.min && propertyValue.max) {
                  var { min: oldMin, max: oldMax } = entitlements[propertyKey]
                  var { min: newMin, max: newMax } = propertyValue
                  
                  entitlements[propertyKey] = {
                    'min': Math.min(newMin, oldMin),
                    'max': Math.max(newMax, oldMax)
                  }
                }
              }
            } else {
              entitlements[propertyKey] = propertyValue
            }
          } catch (error) {
            console.log('VideonDevice ' + this.device_guid + ' entitlements error', Vue.helpers.parseError(error))
          }
        }
      }
    }
    
    // set this.deviceMetricsCacheInterval to services.cloud.metrics.send_rate
    if (entitlements['services.cloud.metrics.send_rate']) {
      this.deviceMetricsCacheInterval = entitlements['services.cloud.metrics.send_rate']
    }
    
    return entitlements
  }
  
  hasFeature(key, currentInstances = 0) {
    var entitlements = this.entitlements()
  
    if (key && entitlements[key]) {
      var propertyValue = entitlements[key]
      
      if (typeof propertyValue === 'boolean') {
        return propertyValue
      } else if (typeof propertyValue === 'number') {
        return (currentInstances < propertyValue) ? true : false
      } else if (typeof propertyValue === 'object') {
        return true
      } else if (Array.isArray(propertyValue)) {
        if (propertyValue.length > 0) {
          return true
        }
      }
    }
    
    return false
  }
  
  featureValue(key, defaultValue = false) {
    var entitlements = this.entitlements()
    
    if (key && entitlements[key]) {
      return entitlements[key]
    }
    
    return defaultValue
  }
  
  featureValueIncludes(key, value) {
    var featureValues = this.featureValue(key)
    return featureValues.includes(value)
  }
  
  featureValueObjectKey(key, objectKey, defaultValue = false) {
    var featureValues = this.featureValue(key)
    
    if (featureValues[objectKey]) {
      return featureValues[objectKey]
    }
    
    return defaultValue
  }
  
  // metrics
  fetchMetrics() {
    return false
  }
  
  // command handling
  runCommand(command, data) {
    if (this.commandGUID || !command || !this.device_guid) {
      return
    }
    console.log('VideonDevice ' + this.device_guid + ' runCommand', command, data)
    
    this.command = command
    this.command_status = 'issued'
    this.command_message = 'Command Issued'
    
    // validSessionCheck
    store.dispatch('user/validSessionCheck').then(() => {
      var commandObject = {'command': command}
      
      // append data if passed in
      commandObject.data = data
      
      Vue.axios.post('/devices/' + this.device_guid + '/commands', commandObject).then((response) => {
        console.log('VideonDevice ' + this.device_guid + ' runCommand response', response)
        this.last_api_request_id = response.data.api_request_id
        
        this.command_status = 'accepted'
        this.command_message = 'Command Accepted'
        this.commandGUID = response.data.command_guid
        
        if (this.commandGUID) {
          this.commandResponseTimer = setTimeout(() => {
            this.commandResponseCheckAttempt++
            this.checkDeviceCommand(this.commandGUID)
          }, this.commandResponseCheckInterval)
        }
      }).catch((error) => {
        console.log('VideonDevice ' + this.device_guid + ' runCommand error', Vue.helpers.parseError(error))
        this.command_status = 'error'
        this.command_message = Vue.helpers.parseError(error)
      })
    }).catch((error) => {
      this.command_status = 'error'
      this.command_message = Vue.helpers.parseError(error)
    })
  }
  
  checkDeviceCommand(command_guid) {
    if (!this.device_guid || !command_guid) {
      return
    }
    
    Vue.axios.get('/devices/' + this.device_guid + '/commands/' + command_guid)
    .then((response) => {
      console.log('VideonDevice ' + this.device_guid + ' checkDeviceCommand response', response)
      this.last_api_request_id = response.data.api_request_id
      
      if (response.data.command.finished === true) {
        console.log('VideonDevice ' + this.device_guid + ' checkDeviceCommand finished', response.data.command)
        this.commandGUID = false
        
        if (response.data.command.response && response.data.command.response.result) {
          var result = response.data.command.response.result
          console.log('VideonDevice ' + this.device_guid + ' checkDeviceCommand result', result)
          
          this.command_status = result.state.toLowerCase()
          this.command_message = result.message
          
          if (this.command_status == 'complete') {
            // sync state after 1s
            setTimeout(() => {
              this.syncDocuments(true)
            }, 1000)
            
            if (this.command == 'update_cloud' || this.command == 'update_device') {
              // update the updates object
              this.checkForUpdates()
            }
          }
        }
      } else {
        // poll until we get updated data, or we timeout...
        if (this.commandResponseCheckAttempt < this.commandResponseCheckMaxAttempts) {
          this.commandResponseTimer = setTimeout(() => {
            this.commandResponseCheckAttempt++
            this.checkDeviceCommand(command_guid)
          }, this.commandResponseCheckInterval)
        } else {
          console.log('VideonDevice ' + this.device_guid + ' checkDeviceCommand did not complete')
          this.command_status = 'error'
          this.command_message = 'No command response in ' + ((this.commandResponseCheckAttempt * this.commandResponseCheckInterval) / 1000) + ' seconds...'
          this.commandGUID = false
        }
      }
    }).catch((error) => {
      console.log('VideonDevice ' + this.device_guid + ' checkDeviceCommand error', Vue.helpers.parseError(error))
    }) 
  }
  
  resetCommandState() {
    if (!this.isCommandProcessing() && !this.commandGUID) {
      console.log('VideonDevice ' + this.device_guid + ' resetCommandState')
      this.command_status = ''
      this.command_message = ''
    }
  }
  
  clearFirmwareUpdateDialogFeedback() {
    this.command_message = ''
    this.command_status = ''
    if (this.command === 'update_device' || this.command === 'update_cloud') {
      this.command = ''
    }
  }
  
}